import React, { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogTitle } from './dialog';
import { Button } from './button';
import { Text } from './text';
import { retarifarComisionesEnLotes } from '../api/comisiones';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

/**
 * RetarifaComisionesDialog — after a tarifario change, lists the fianzas whose
 * PENDING comisión was priced off the replaced pricing and lets the owner pick
 * which to update at the new %. Cobradas and manual overrides never appear
 * (they are historical facts; the backend refuses them anyway).
 */
export function RetarifaComisionesDialog({ isOpen, onClose, plan, tarifaId }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [progreso, setProgreso] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set((plan || []).map((p) => p.fianzaId)));
      setError(null);
      setProgreso(null);
    }
  }, [isOpen, plan]);

  const toggle = (fianzaId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fianzaId)) next.delete(fianzaId);
      else next.add(fianzaId);
      return next;
    });
  };

  const confirm = async () => {
    const cambios = (plan || [])
      .filter((p) => selected.has(p.fianzaId))
      .map((p) => ({ fianzaId: p.fianzaId, comisionPct: p.pctNuevo }));
    if (!cambios.length) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    setProgreso({ hechas: 0, total: cambios.length });
    try {
      // Sent in batches — the endpoint takes at most 200 fianzas per call.
      await retarifarComisionesEnLotes({ cambios, tarifaId }, (hechas, total) =>
        setProgreso({ hechas, total })
      );
      onClose();
    } catch (e) {
      console.error('retarifa failed', e);
      const aplicadas = e.parcial?.updatedFianzas || 0;
      setError(
        aplicadas
          ? `${e.message || 'No se pudo actualizar la comisión.'} Se actualizaron ` +
            `${aplicadas} de ${cambios.length} fianzas; vuelve a confirmar para ` +
            'aplicar las restantes.'
          : e.message || 'No se pudo actualizar la comisión.'
      );
    } finally {
      setBusy(false);
      setProgreso(null);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => !busy && onClose()} size="2xl">
      <DialogTitle>Actualizar comisiones pendientes</DialogTitle>
      <DialogBody>
        <Text className="text-sm text-gray-600 mb-3">
          {plan?.length} fianza{plan?.length !== 1 ? 's' : ''} con comisión
          pendiente calculada con el tarifario anterior. Las cobradas no se tocan.
        </Text>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="border border-gray-200 rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3">Fianza</th>
                <th className="py-2 px-3 text-right">%</th>
                <th className="py-2 px-3 text-right">Comisión</th>
              </tr>
            </thead>
            <tbody>
              {(plan || []).map((p) => (
                <tr
                  key={p.fianzaId}
                  className="border-b border-gray-100 last:border-0 cursor-pointer hover:bg-zinc-50"
                  onClick={() => toggle(p.fianzaId)}
                >
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.fianzaId)}
                      onChange={() => toggle(p.fianzaId)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-azul-surelia border-gray-300 rounded"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-gray-800">{p.numeroFianza || 'Sin número'}</div>
                    {p.nombre && <div className="text-xs text-gray-400">{p.nombre}</div>}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    <span className="text-gray-400">{p.pctAnterior != null ? `${p.pctAnterior}%` : '—'}</span>
                    <span className="mx-1 text-gray-300">→</span>
                    <span className="font-semibold text-gray-800">{p.pctNuevo}%</span>
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    <span className="text-gray-400">{fmtMXN(p.montoAnterior)}</span>
                    <span className="mx-1 text-gray-300">→</span>
                    <span className={`font-semibold ${p.montoNuevo >= p.montoAnterior ? 'text-emerald-700' : 'text-red-600'}`}>
                      {fmtMXN(p.montoNuevo)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button plain onClick={onClose} disabled={busy}>
            No actualizar
          </Button>
          <Button onClick={confirm} disabled={busy || selected.size === 0}>
            {busy
              ? progreso
                ? `Actualizando ${progreso.hechas} de ${progreso.total}...`
                : 'Actualizando...'
              : `Actualizar ${selected.size}`}
          </Button>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default RetarifaComisionesDialog;
